import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { CatalogosApi } from "../../hooks/catalogos";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { VariacaoPromocao } from "../../types/Produto";
import Loading from "../Loading/Loading";
import Modal from "../Modal/Modal";
import Table from "../Table/Table";
import { Button } from "../Button";
import { ReactComponent as Voltar } from "../../assets/newImages/icons/angle-down.svg";
import { ContainerModalVariacoesPromocao } from "./styles";

interface ModalVariacoesPromocaoProps {
  isOpen: boolean;
  setModal: (value: boolean) => void;
  idProduto: number;
  nomeProduto?: string;
  idTabela?: string | number;
  moeda?: string;
  selecionadas?: number[];
  bloqueadas?: number[];
  onAplicar: (variacoes: number[]) => void;
}

const nomeVariacao = (variacao: VariacaoPromocao) =>
  [variacao.variacao, variacao.subvariacao].filter((v) => v && v.trim()).join(" / ") ||
  "Variação sem nome";

export const ModalVariacoesPromocao: React.FC<ModalVariacoesPromocaoProps> = ({
  isOpen,
  setModal,
  idProduto,
  nomeProduto,
  idTabela,
  moeda = "R$",
  selecionadas,
  bloqueadas,
  onAplicar,
}) => {
  const { theme } = useTheme();
  const [carregando, setCarregando] = useState(false);
  const [variacoes, setVariacoes] = useState<VariacaoPromocao[]>([]);
  const [naPromocao, setNaPromocao] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen || !idProduto) return;

    let cancelado = false;
    setCarregando(true);
    setVariacoes([]);

    CatalogosApi()
      .getVariacoesProduto(idProduto, idTabela)
      .then((resposta) => {
        if (cancelado) return;
        const lista = resposta?.variacoes || [];
        const jaUsadas = new Set(bloqueadas || []);
        const disponiveis = lista.filter((v) => !jaUsadas.has(v.id));
        setVariacoes(lista);
        setNaPromocao(
          selecionadas && selecionadas.length
            ? disponiveis.filter((v) => selecionadas.includes(v.id)).map((v) => v.id)
            : disponiveis.map((v) => v.id)
        );
      })
      .catch(() => {
        if (cancelado) return;
        setModal(false);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar as variações desse produto.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idProduto, idTabela]);

  const indisponiveis = useMemo(() => new Set(bloqueadas || []), [bloqueadas]);

  const livres = useMemo(
    () => variacoes.filter((v) => !indisponiveis.has(v.id)),
    [variacoes, indisponiveis]
  );

  const todasNaPromocao = useMemo(
    () => livres.length > 0 && naPromocao.length === livres.length,
    [livres, naPromocao]
  );

  const alternar = (id: number) => {
    setNaPromocao((atual) =>
      atual.includes(id) ? atual.filter((v) => v !== id) : [...atual, id]
    );
  };

  const handleAplicar = () => {
    if (!naPromocao.length) {
      return Swal.fire({
        title: "Ops!",
        text: "Deixe pelo menos uma variação na promoção.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    onAplicar(todasNaPromocao && !indisponiveis.size ? [] : naPromocao);
    setModal(false);
  };

  const colunas = [
    {
      key: "nome",
      label: "Nome da variação",
      mask: (value: any) => value,
    },
    {
      key: "estoque",
      label: "Estoque",
      mask: (value: string) => <span className="fonteTexto">{value}</span>,
    },
    {
      key: "ajuste",
      label: "Ajuste",
      mask: (value: any) => value,
    },
  ];

  const linhas = variacoes.map((variacao) => {
    const bloqueada = indisponiveis.has(variacao.id);
    const incluida = naPromocao.includes(variacao.id);
    const preco =
      variacao.preco === null
        ? null
        : `${moeda} ${(variacao.preco / 100).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;

    return {
      nome: (
        <span className={`celula-nome ${bloqueada ? "bloqueada" : ""}`}>
          <span className="fonteTexto">{nomeVariacao(variacao)}</span>
          {(variacao.sku || preco) && (
            <span className="detalhe fonteRotulos">
              {variacao.sku ? `SKU ${variacao.sku}` : null}
              {variacao.sku && preco ? " · " : null}
              {preco}
            </span>
          )}
        </span>
      ),
      estoque:
        variacao.estoque === null ? "Sem controle" : String(variacao.estoque),
      ajuste: bloqueada ? (
        <span className="em-outra fonteRotulos">Em outra promoção</span>
      ) : (
        <Button
          title={incluida ? "Remover promoção" : "Aplicar promoção"}
          className={`acao-variacao fonteTexto ${incluida ? "remover" : "adicionar"}`}
          onClick={() => alternar(variacao.id)}
        />
      ),
    };
  });

  return (
    <Modal isOpen={isOpen} setModal={setModal} width="560px" maxWidth="90vw">
      <ContainerModalVariacoesPromocao theme={theme}>
        <div className="titulo fonteSubtitulo">Produto com variações</div>
        <div className="subtitulo fonteTexto">
          Defina quais variações estarão inclusas na promoção
          {nomeProduto ? <> de <strong>{nomeProduto}</strong></> : null}.
        </div>

        {carregando ? (
          <div className="carregando">
            <Loading relative />
          </div>
        ) : livres.length === 0 ? (
          <div className="vazio fonteRotulos">
            {variacoes.length
              ? "Todas as variações desse produto já estão em outra promoção."
              : "Esse produto não tem variações ativas."}
          </div>
        ) : (
          <div className="tabela scrollBonito">
            <Table
              columns={colunas}
              data={linhas}
              sortable={false}
              selectable={false}
              button={false}
              page={1}
              itensPorPagina={-1}
            />
          </div>
        )}

        <div className="botoes">
          <div className="cancelar fonteTexto" onClick={() => setModal(false)}>
            <Voltar /> Cancelar
          </div>
          <Button
            title="Aplicar"
            className="fonteTexto"
            disabled={carregando || livres.length === 0}
            onClick={handleAplicar}
          />
        </div>
      </ContainerModalVariacoesPromocao>
    </Modal>
  );
};

export default ModalVariacoesPromocao;
